/**
 * Path to Performance Data Validation Utilities
 * Validation logic for P2P action items and assessments
 */

import type { ActionItemData, AssessmentData } from "../../store/slices/pathToPerformanceSlice";
import { isActionItemSelected } from "./pathToPerformanceHelpers";

/**
 * Calculate minimum p2pRating from selected action items
 * The rating is the minimum category level from all selected items
 * 
 * @param actionItems - Array of action item data
 * @returns Minimum rating (0 if no items selected)
 * 
 * @example
 * ```typescript
 * const rating = calculateP2PRating(actionItems);
 * ```
 */
export const calculateP2PRating = (actionItems: ActionItemData[]): number => {
  const selectedLevels = actionItems
    .filter(item => isActionItemSelected(item.actionNeeded, Number(item.categoryLevel)))
    .map((item) =>
      typeof item.categoryLevel === "number"
        ? item.categoryLevel
        : Number.parseInt(String(item.categoryLevel), 10),
    );

  if (selectedLevels.length === 0) {
    return 0;
  }

  return Math.min(...selectedLevels);
};

/**
 * Validate that at least one action item is selected
 * 
 * @param actionItems - Array of action item data
 * @returns True if at least one action item is selected
 * 
 * @example
 * ```typescript
 * if (!validateActionItems(actionItems)) {
 *   // Show error
 * }
 * ```
 */
export const validateActionItems = (actionItems: ActionItemData[]): boolean => {
  return actionItems.some(
    (item) => isActionItemSelected(item.actionNeeded, Number(item.categoryLevel)),
  );
};

/**
 * Check if action items have changed from initial values
 * 
 * @param actionItems - Current action items
 * @param initialActionItems - Initial action items from API
 * @returns True if any action items have changed
 */
export const hasActionItemsChanged = (
  actionItems: ActionItemData[],
  initialActionItems: ActionItemData[],
): boolean => {
  return actionItems.some((item, index) => {
    const initial = initialActionItems[index];
    if (!initial) return true;
    
    return (
      item.categoryLevel !== initial.categoryLevel ||
      item.actionNeeded !== initial.actionNeeded ||
      item.notes !== initial.notes
    );
  });
};

/**
 * Check if assessments have changed from initial values
 * Only checks the current period (editable assessment)
 * 
 * @param assessments - Current assessments
 * @param initialAssessments - Initial assessments from API
 * @returns True if current assessment has changed
 */
export const hasAssessmentsChanged = (
  assessments: AssessmentData[],
  initialAssessments: AssessmentData[],
): boolean => {
  const currentAssessment = assessments.find(
    (a) => a.period.toLowerCase() === "current",
  );
  const initialCurrentAssessment = initialAssessments.find(
    (a) => a.period.toLowerCase() === "current",
  );

  return !!(
    currentAssessment &&
    initialCurrentAssessment &&
    currentAssessment.assessmentNotes !== initialCurrentAssessment.assessmentNotes
  );
};

/**
 * Check if any P2P data has changed from initial values
 * 
 * @param actionItems - Current action items
 * @param initialActionItems - Initial action items
 * @param assessments - Current assessments
 * @param initialAssessments - Initial assessments
 * @returns True if any data has changed
 */
export const hasP2PDataChanged = (
  actionItems: ActionItemData[],
  initialActionItems: ActionItemData[],
  assessments: AssessmentData[],
  initialAssessments: AssessmentData[],
): boolean => {
  return (
    hasActionItemsChanged(actionItems, initialActionItems) ||
    hasAssessmentsChanged(assessments, initialAssessments)
  );
};
